import { SKILLS, HEROES, ITEMS, BACKPACK_SLOTS, MAX_ACTIVES, MAX_AUTOS, RANK_COST, RESPEC_FAME, RESPEC_FREE_BELOW_RANK, aspectCost, aspectOptions, aspectPath, canEquipItem, heroPool, rankPoints, traitPool, type BuildOp, type HeroId, type ItemInst, type ItemSlot, type KitOp, type Modifier, type Rarity } from '@knight/shared';
import { t, tn } from '../i18n';
import { confirmDialog } from '../app/state';
import { Btn, Icon, aspectTip, blessingTip, itemTip, keystoneTip, skillTip, skillDesc, affixText } from './common';

/** One skill of the kit, in whatever holds it: a living hero or a saved one. */
export interface KitSkill {
  id: string;
  /** in-realm rank of the skill; absent for a resting hero */
  level?: number;
  aspects: string[];
  auto?: boolean;
}

/** An item as both a living hero and a saved one can show it. */
export interface GearItem {
  def: string;
  rarity: Rarity;
  affixes: Modifier[];
  uid?: number;
}

export function asInst(item: GearItem, fallbackUid = 0): ItemInst {
  return { uid: item.uid ?? fallbackUid, def: item.def, rarity: item.rarity, affixes: item.affixes ?? [] };
}

export interface KitProps {
  heroId: HeroId;
  rank: number;
  points: number;
  actives: KitSkill[];
  autos: KitSkill[];
  traits: { id: string; level: number }[];
  blessings?: string[];
  keystones?: string[];
  /** a spectator or a dead hero may look but not touch */
  readonly?: boolean;
  onOp: (op: KitOp) => void;
}

function SkillRow(props: { s: KitSkill; kit: KitProps; isActive: boolean }) {
  const { s, kit } = props;
  const path = aspectPath(s.id, s.aspects);
  const ring = (path.length + 1) as 1 | 2;
  const options = path.length < 2 ? aspectOptions(s.id, s.aspects) : [];
  const cost = aspectCost(s.aspects);
  return (
    <div class="kit-skill">
      <div class="row">
        <Icon kind="skill" id={s.id} size="sm" level={s.level} tip={skillTip(s.id, s.level, s.aspects)} />
        <div class="col grow" style={{ gap: 0 }}>
          <b>{tn('skill', s.id)}</b>
          <span class="small muted">{path.length ? path.map((a) => tn('aspect', a.id)).join(' → ') : skillDesc(s.id, s.level, s.aspects)}</span>
        </div>
        {props.isActive && !kit.readonly && (
          <label class="small muted row clickable" style={{ gap: 4 }} title={t('label.autoCastHint')}>
            <input type="checkbox" checked={!!s.auto} onChange={(e) => kit.onOp({ op: 'auto', id: s.id, on: (e.target as HTMLInputElement).checked })} />
            {t('label.autoCast')}
          </label>
        )}
      </div>
      {options.length > 0 && (
        <div class="row wrap" style={{ gap: 6, paddingLeft: 36 }}>
          <span class="small muted">{t(ring === 1 ? 'label.ring1' : 'label.ring2')}:</span>
          {options.map((a) => {
            const can = !kit.readonly && kit.points >= cost;
            return (
              <div class={'aspect-opt' + (can ? '' : ' off')} onClick={() => can && kit.onOp({ op: 'aspect', id: s.id, aspect: a.id })}>
                <Icon kind="aspect" id={a.id} skill={s.id} size="sm" tip={aspectTip(s.id, a.id, ring, cost)} />
                <span class="small">{tn('aspect', a.id)}</span>
                <span class="small gold">{cost}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Rank points: skills of the hero's pool, their two aspect rings and traits. */
export function KitPanel(props: { kit: KitProps; fame?: number; onRespec?: () => void }) {
  const kit = props.kit;
  const owned = new Set([...kit.actives, ...kit.autos].map((s) => s.id));
  const pool = heroPool(kit.heroId).filter((id) => !owned.has(id) && SKILLS[id]);
  const traits = traitPool(kit.heroId);
  const respecFree = kit.rank < RESPEC_FREE_BELOW_RANK;
  return (
    <>
      <div class="rankbox">
        <div class="row">
          <b class="void">
            {t('label.rankTrack')} {kit.rank}
          </b>
          <div class="grow" />
          <b class={kit.points > 0 ? 'gold' : 'muted'}>{t('label.pointsFree', { n: kit.points, total: rankPoints(kit.rank) })}</b>
        </div>
        <div class="small muted">{t('label.rankHint')}</div>
      </div>
      <h3>
        {t('label.actives')}{' '}
        <span class="small muted">
          {kit.actives.length}/{MAX_ACTIVES}
        </span>
      </h3>
      <div class="list">
        {kit.actives.map((s) => (
          <SkillRow s={s} kit={kit} isActive />
        ))}
      </div>
      {kit.autos.length > 0 && (
        <>
          <h3>
            {t('label.autos')}{' '}
            <span class="small muted">
              {kit.autos.length}/{MAX_AUTOS}
            </span>
          </h3>
          <div class="list">
            {kit.autos.map((s) => (
              <SkillRow s={s} kit={kit} isActive={false} />
            ))}
          </div>
        </>
      )}
      {pool.length > 0 && (
        <>
          <h3>{t('label.kitPool')}</h3>
          <div class="small muted">{t('label.kitPoolHint', { cost: RANK_COST.skill })}</div>
          <div class="row wrap" style={{ gap: 6 }}>
            {pool.map((id) => {
              const def = SKILLS[id];
              const full = def.kind === 'active' ? kit.actives.length >= MAX_ACTIVES : kit.autos.length >= MAX_AUTOS;
              const can = !kit.readonly && kit.points >= RANK_COST.skill && !full;
              return (
                <div class={'aspect-opt' + (can ? '' : ' off')} onClick={() => can && kit.onOp({ op: 'unlock', id })} title={full ? t(def.kind === 'active' ? 'build.activesFull' : 'build.autosFull') : undefined}>
                  <Icon kind="skill" id={id} size="sm" tip={skillTip(id, 1)} />
                  <span class="small">{tn('skill', id)}</span>
                  <span class="small muted">{def.kind === 'active' ? t('label.activeShort') : t('label.autoShort')}</span>
                  <span class="small gold">{RANK_COST.skill}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
      <h3>{t('label.traits')}</h3>
      <div class="row wrap" style={{ gap: 6 }}>
        {traits.map((id) => {
          const def = SKILLS[id];
          const cur = kit.traits.find((x) => x.id === id)?.level ?? 0;
          const can = !kit.readonly && kit.points >= RANK_COST.trait && cur < def.maxLevel;
          return (
            <div class={'aspect-opt' + (can ? '' : ' off') + (cur > 0 ? ' owned' : '')} onClick={() => can && kit.onOp({ op: 'trait', id })}>
              <Icon kind="skill" id={id} size="sm" level={cur || undefined} tip={skillTip(id, cur || 1)} />
              <span class="small">{tn('skill', id)}</span>
              <span class="small muted">
                {cur}/{def.maxLevel}
              </span>
              {cur < def.maxLevel && <span class="small gold">+{RANK_COST.trait}</span>}
            </div>
          );
        })}
      </div>
      {!!kit.blessings?.length && (
        <>
          <h3>{t('label.blessings')}</h3>
          <div class="small muted">{t('label.blessingsHint')}</div>
          <div class="row wrap" style={{ gap: 6 }}>
            {groupBlessings(kit.blessings).map(([id, n]) => (
              <div class="aspect-opt owned">
                <Icon kind="blessing" id={id} size="sm" count={n > 1 ? n : undefined} tip={blessingTip(id, n)} />
                <span class="small">{tn('blessing', id)}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {!!kit.keystones?.length && (
        <>
          <h3>{t('label.keystones')}</h3>
          <div class="row wrap" style={{ gap: 6 }}>
            {kit.keystones.map((k) => (
              <div class="aspect-opt owned">
                <Icon kind="keystone" id={k} size="sm" tip={keystoneTip(k)} />
                <span class="small">{tn('keystone', k)}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {!kit.readonly && (
        <div class="row" style={{ marginTop: 6 }}>
          <Btn
            small
            danger
            disabled={!respecFree && props.fame !== undefined && props.fame < RESPEC_FAME}
            onClick={() => {
              void confirmDialog({ title: t('btn.respec'), text: t(respecFree ? 'confirm.respecFree' : 'confirm.respecPaid', { fame: RESPEC_FAME }), ok: t('btn.respec'), danger: true }).then((ok) => {
                if (!ok) return;
                if (props.onRespec) props.onRespec();
                else kit.onOp({ op: 'respec' });
              });
            }}
          >
            {t('btn.respec')}
          </Btn>
          <span class="small muted">{respecFree ? t('label.respecFree', { rank: RESPEC_FREE_BELOW_RANK }) : t('label.respecCost', { fame: RESPEC_FAME })}</span>
        </div>
      )}
    </>
  );
}

export function groupBlessings(list: string[]): [string, number][] {
  const m = new Map<string, number>();
  for (const id of list) m.set(id, (m.get(id) ?? 0) + 1);
  return [...m.entries()];
}

// ------------------------------------------------------------------ gear

export interface GearProps {
  heroId: HeroId;
  weapon: GearItem;
  armor?: GearItem;
  charm?: GearItem;
  backpack: GearItem[];
  readonly?: boolean;
  /** takes the backpack index */
  onEquip: (index: number) => void;
  onUnequip: (slot: ItemSlot) => void;
  /** in a realm the hero can throw a piece on the ground; resting heroes use the vault instead */
  onDrop?: (index: number) => void;
}

function GearRow(props: { item: GearItem; heroId: HeroId; slot: ItemSlot; worn: boolean; index: number; g: GearProps }) {
  const { item, g } = props;
  const def = ITEMS[item.def];
  const fits = canEquipItem(props.heroId, item.def);
  const base = HEROES[props.heroId].weapon;
  const plain = props.slot === 'weapon' && item.def === base && item.rarity === 0 && !(item.affixes ?? []).length;
  return (
    <div class={'item' + (fits ? '' : ' unfit')}>
      <Icon kind="item" id={item.def} size="sm" tip={itemTip(asInst(item, props.index + 1))} dim={!fits} />
      <div class="col grow" style={{ gap: 0 }}>
        <b class={'rar' + item.rarity}>{tn('item', item.def)}</b>
        <span class="small muted">
          {def ? t('label.' + def.slot) : ''}
          {def?.slot === 'weapon' ? ` · ${t('label.wclass.' + def.wclass)}` : ''}
          {fits ? '' : ` · ${t('label.notForHero')}`}
        </span>
        {(item.affixes ?? []).map((a) => (
          <span class="small gold">{affixText(a.stat, a.add, a.inc)}</span>
        ))}
      </div>
      {!g.readonly && !props.worn && (
        <Btn small disabled={!fits} title={fits ? undefined : t('build.wrongWeapon')} onClick={() => g.onEquip(props.index)}>
          {t('btn.equip')}
        </Btn>
      )}
      {!g.readonly && props.worn && !plain && (
        <Btn small ghost disabled={g.backpack.length >= BACKPACK_SLOTS} title={t('btn.takeOff')} onClick={() => g.onUnequip(props.slot)}>
          {t('btn.takeOff')}
        </Btn>
      )}
      {!g.readonly && !props.worn && g.onDrop && (
        <Btn small ghost title={t('btn.drop')} onClick={() => g.onDrop!(props.index)}>
          ✕
        </Btn>
      )}
    </div>
  );
}

export function GearPanel(props: { gear: GearProps }) {
  const g = props.gear;
  const hero = HEROES[g.heroId];
  const worn: [ItemSlot, GearItem | undefined][] = [
    ['weapon', g.weapon],
    ['armor', g.armor],
    ['charm', g.charm],
  ];
  return (
    <>
      <h3>{t('label.equipped')}</h3>
      <div class="small muted">{t('label.heroWeapons', { list: hero.weapons.map((w) => t('label.wclass.' + w)).join(', ') })}</div>
      <div class="list">
        {worn.map(([slot, it], i) =>
          it ? (
            <GearRow item={it} heroId={g.heroId} slot={slot} worn index={-1 - i} g={g} />
          ) : (
            <div class="item muted small">
              {t('label.' + slot)}: {t('label.empty')}
            </div>
          ),
        )}
      </div>
      <h3>
        {t('label.backpack')}{' '}
        <span class="small muted">
          {g.backpack.length}/{BACKPACK_SLOTS}
        </span>
      </h3>
      <div class="small muted">{t('label.backpackHint')}</div>
      <div class="list">
        {g.backpack.length === 0 && <div class="item muted small">{t('label.empty')}</div>}
        {g.backpack.map((it, i) => {
          const def = ITEMS[it.def];
          return <GearRow item={it} heroId={g.heroId} slot={(def?.slot ?? 'charm') as ItemSlot} worn={false} index={i} g={g} />;
        })}
      </div>
    </>
  );
}

/** Turns a kit op into the in-realm build op the server expects. */
export function toBuildOp(op: KitOp): BuildOp {
  return op;
}
